import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import '../../assets/css/elements/file_upload.css';

// File size limits - increased to 15MB to match backend
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB
// const _ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/svg+xml'];
const ALLOWED_DOC_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/svg+xml', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

const FileUpload = ({
    label,
    field,
    accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx',
    onUpload,
    currentFile,
    required = false,
    disabled = false,
    maxSize = MAX_FILE_SIZE,
    allowedTypes = ALLOWED_DOC_TYPES,
    hint = 'PDF, JPG, PNG, or DOC (max 15MB)',
    showPreview = true,
    _variant = 'default', // 'default' | 'compact' | 'card' - reserved for future use
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);
    const fileInputRef = useRef(null);

    const validateFile = useCallback((file) => {
        setError(null);

        if (!file) {
            return { valid: false, error: 'No file selected' };
        }

        // Check file size
        if (file.size > maxSize) {
            const sizeMB = (maxSize / (1024 * 1024)).toFixed(0);
            return { 
                valid: false, 
                error: `File is too large. Maximum size is ${sizeMB}MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.` 
            };
        }

        // Check file type
        if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
            const typeNames = allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ');
            return { 
                valid: false, 
                error: `Invalid file type. Please upload: ${typeNames}` 
            };
        }

        return { valid: true };
    }, [maxSize, allowedTypes]);

    const handleFile = useCallback(async (file) => {
        const validation = validateFile(file);
        
        if (!validation.valid) {
            setError(validation.error);
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);
        setError(null);

        try {
            // Generate preview for images
            if (showPreview && file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => setPreview(e.target.result);
                reader.readAsDataURL(file);
            } else if (file.type === 'application/pdf') {
                setPreview('pdf');
            }

            // Simulate progress for local file selection
            // In real upload scenario, this would come from axios/fetch progress
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 50);

            // Call the parent's upload handler
            await onUpload(field, file);
            
            clearInterval(progressInterval);
            setUploadProgress(100);
            
            // Reset progress after a moment
            setTimeout(() => {
                setUploadProgress(0);
                setIsUploading(false);
            }, 500);

        } catch (err) {
            console.error('File upload error:', err);
            setIsUploading(false);
            setUploadProgress(0);
            
            // Better error messages based on error type
            let errorMessage = 'Failed to process file. ';
            
            if (err.message?.includes('Network') || err.code === 'ERR_NETWORK') {
                errorMessage = 'Network error. Please check your internet connection and try again.';
            } else if (err.message?.includes('timeout')) {
                errorMessage = 'Upload timed out. The file might be too large or your connection is slow.';
            } else if (err.response?.status === 413) {
                errorMessage = 'File is too large for the server. Please try a smaller file.';
            } else if (err.response?.status === 415) {
                errorMessage = 'File type not supported by the server.';
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage += err.message;
            } else {
                errorMessage += 'Please try again.';
            }
            
            setError(errorMessage);
        }
    }, [field, onUpload, showPreview, validateFile]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (disabled || isUploading) return;

        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
    }, [disabled, isUploading, handleFile]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled && !isUploading) {
            setIsDragging(true);
        }
    }, [disabled, isUploading]);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleClick = () => {
        if (!disabled && !isUploading) {
            fileInputRef.current?.click();
        }
    };

    const handleInputChange = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
        // Reset input so same file can be selected again
        e.target.value = '';
    };

    const handleRemove = (e) => {
        e.stopPropagation();
        setPreview(null);
        setError(null);
        onUpload(field, null);
    };

    const getFileName = () => {
        if (currentFile?.name) return currentFile.name;
        if (typeof currentFile === 'string') return currentFile.split('/').pop();
        return null;
    };

    const fileName = getFileName();
    const hasFile = currentFile || preview;

    return (
        <div className="file-upload-wrapper">
            {label && (
                <label className="file-upload-label">
                    {label}
                    {required && <span className="required-asterisk">*</span>}
                </label>
            )}
            
            <div
                className={`file-upload-zone ${isDragging ? 'dragging' : ''} ${hasFile ? 'has-file' : ''} ${disabled ? 'disabled' : ''} ${error ? 'has-error' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={handleClick}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleInputChange}
                    disabled={disabled || isUploading}
                    className="file-input-hidden"
                />

                {isUploading ? (
                    <div className="upload-progress">
                        <div className="progress-bar">
                            <div 
                                className="progress-fill" 
                                style={{ width: `${uploadProgress}%` }}
                            />
                        </div>
                        <span className="progress-text">Uploading... {uploadProgress}%</span>
                    </div>
                ) : hasFile ? (
                    <div className="file-preview">
                        {preview && preview !== 'pdf' ? (
                            <img src={preview} alt="Preview" className="preview-image" />
                        ) : preview === 'pdf' ? (
                            <div className="pdf-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                    <polyline points="10 9 9 9 8 9"/>
                                </svg>
                            </div>
                        ) : (
                            <div className="file-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                                </svg>
                            </div>
                        )}
                        <div className="file-info">
                            <span className="file-name">{fileName || 'File selected'}</span>
                            <span className="file-size">{currentFile?.size ? `${(currentFile.size / (1024 * 1024)).toFixed(2)} MB` : ''}</span>
                            <button 
                                type="button" 
                                className="remove-file-btn"
                                onClick={handleRemove}
                                disabled={disabled}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                                Remove
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="upload-placeholder">
                        <div className="upload-icon">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="17 8 12 3 7 8"/>
                                <line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                        </div>
                        <p className="upload-text">
                            <span className="upload-link">Click to upload</span> or drag and drop
                        </p>
                        <p className="upload-hint">{hint}</p>
                    </div>
                )}
            </div>

            {error && (
                <div className="file-upload-error">
                    <span className="error-icon">⚠️</span>
                    <span className="error-text">{error}</span>
                </div>
            )}
        </div>
    );
};

FileUpload.propTypes = {
    label: PropTypes.string,
    field: PropTypes.string.isRequired,
    accept: PropTypes.string,
    onUpload: PropTypes.func.isRequired,
    currentFile: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    maxSize: PropTypes.number,
    allowedTypes: PropTypes.arrayOf(PropTypes.string),
    hint: PropTypes.string,
    showPreview: PropTypes.bool,
    _variant: PropTypes.oneOf(['default', 'compact', 'card']),
};

export default FileUpload;
